import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { api } from "../api/api";
import { message } from "antd";

interface FeatureRoles {
  featureKey: string;
  roles: string[];
}

interface FeatureVisibilityContextType {
  enabledFeatures: Set<string>;
  featureRoles: Map<string, string[]>; // featureKey -> roles
  isFeatureEnabled: (featureKey: string) => boolean;
  getFeatureRoles: (featureKey: string) => string[] | null; // Получить роли для фичи из API, или null если нет
  loading: boolean;
  refresh: () => Promise<void>;
}

const defaultContext: FeatureVisibilityContextType = {
  enabledFeatures: new Set<string>(),
  featureRoles: new Map<string, string[]>(),
  isFeatureEnabled: () => true, // По умолчанию все включено до загрузки
  getFeatureRoles: () => null,
  loading: true,
  refresh: async () => {},
};

const FeatureVisibilityContext = createContext(defaultContext);

export const FeatureVisibilityProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [enabledFeatures, setEnabledFeatures] = useState<Set<string>>(new Set());
  const [featureRoles, setFeatureRoles] = useState<Map<string, string[]>>(new Map());
  const [loading, setLoading] = useState(true);

  const loadEnabledFeatures = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setEnabledFeatures(new Set());
        setFeatureRoles(new Map());
        setLoading(false);
        return;
      }

      const response = await api.get("/settings/features/enabled");
      const enabledKeys = response.data.enabledFeatures || [];
      setEnabledFeatures(new Set(enabledKeys));
      
      // Загружаем роли для всех фич (если есть в ответе)
      if (response.data.features && Array.isArray(response.data.features)) {
        const rolesMap = new Map<string, string[]>();
        response.data.features.forEach((feature: FeatureRoles) => {
          rolesMap.set(feature.featureKey, feature.roles || []);
        });
        setFeatureRoles(rolesMap);
      }
    } catch (error: any) {
      console.error("Ошибка при загрузке включенных фич:", error);
      // В случае ошибки считаем все фичи включенными
      setEnabledFeatures(new Set());
      setFeatureRoles(new Map());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEnabledFeatures();
  }, [loadEnabledFeatures]);

  const isFeatureEnabled = useCallback(
    (featureKey: string): boolean => {
      // Если еще загружается, возвращаем true (по умолчанию все включено)
      if (loading) {
        return true;
      }
      // Если множество пустое, считаем что все включено (fallback)
      if (enabledFeatures.size === 0) {
        return true;
      }
      return enabledFeatures.has(featureKey);
    },
    [enabledFeatures, loading]
  );

  const getFeatureRoles = useCallback(
    (featureKey: string): string[] | null => {
      return featureRoles.get(featureKey) || null;
    },
    [featureRoles]
  );

  const refresh = useCallback(async () => {
    await loadEnabledFeatures();
  }, [loadEnabledFeatures]);

  return (
    <FeatureVisibilityContext.Provider
      value={{
        enabledFeatures,
        featureRoles,
        isFeatureEnabled,
        getFeatureRoles,
        loading,
        refresh,
      }}
    >
      {children}
    </FeatureVisibilityContext.Provider>
  );
};

export const useFeatureVisibility = (): FeatureVisibilityContextType => {
  return useContext(FeatureVisibilityContext);
};
